import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ViewModels } from "../configs/constants/ViewModels";
import Contract, {
	CreateObRuleRequest,
	CreateOrUpdateContractRequest,
	DeleteObRuleRequest,
	Price,
	UpdateObRuleRequest,
	BatchUpdateObRulesRequest,
} from "../models/Contract";
import { ApiErrorResponse } from "../models/ApiResponse";
import { apiStore } from "../stores/api.store";
import { toast } from "../utils/toast";
import { useAuthStore } from "../stores/auth.store";

/**
 * Coerce user-entered price minHours ("1,5") to numbers the API accepts
 * (was ContractStore.toAPIModel). Mutates the given prices in place.
 */
export function normalizeContractPrices(prices: Price[]) {
	prices.forEach((price: Price) => {
		const hours = price?.minHours?.toString().replace(/,/g, ".");
		if (hours) price.minHours = parseFloat(hours);
		else price.minHours = 0;
	});
}

export function useContracts(
	params: {
		page?: number;
		pageSize?: number;
		active?: boolean;
		sort?: string;
		order?: 1 | -1;
		keyword?: string;
		isDeleted?: boolean;
		view?: ViewModels;
	} = {},
	enabled?: boolean
) {
	const { data, ...rest } = useQuery(
		["contracts", params],
		() =>
			apiStore
				.get<{ contracts: Contract[]; totalCount: number }>({
					path: `/contract`,
					query: {
						...params,
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{ enabled }
	);

	return {
		...data,
		...rest,
	};
}

export function useObContracts(contractId: string, enabled?: boolean) {
	const authStore = useAuthStore();
	const { data, ...rest } = useQuery(
		["contracts", contractId, "useObContracts"],
		() =>
			apiStore
				.get<any>({
					path: `/contract/${contractId}/ob-rule`,
					query: {
						includeFallback: true,
					},
					isAuthRequired: true,
				})
				.then((d) => {
					return d.data;
				}),

		{ enabled }
	);

	return {
		data: data,
		...rest,
	};
}

type GetContractParams = {
	contractId?: string;
	inheritFromFallback?: boolean;
};

export function useContract(params: GetContractParams, enabled?: boolean) {
	const { data, ...rest } = useQuery(
		["contracts", params],
		() =>
			apiStore
				.get<Contract>({
					path: `/contract/${params.contractId}`,
					query: {
						expand: params.inheritFromFallback ? "terms_prices.inherited" : "terms_prices",
					},
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{ enabled }
	);

	return {
		contract: data,
		...rest,
	};
}

export function useDeleteContract() {
	const queryClient = useQueryClient();

	return useMutation(
		(contractId: string) =>
			apiStore
				.delete({
					path: `/contract/${contractId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
		}
	);
}

export function useDeleteObContract() {
	const queryClient = useQueryClient();

	return useMutation(
		(param: DeleteObRuleRequest) =>
			apiStore
				.delete({
					path: `/contract/${param.contactId}/ob-rule/${param.ruleId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
		}
	);
}

export function useCreateObRule(contractId: string) {
	const queryClient = useQueryClient();

	return useMutation(
		(param: CreateObRuleRequest) =>
			apiStore
				.post({
					path: `/contract/${contractId}/ob-rule`,
					isAuthRequired: true,
					body: param,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
			onError: (error: any) => {
				toast.errorGenericObRule(error?.error?.detail || "Something went wrong");
			},
		}
	);
}

export function useCreateObRuleUpdate(contractId: string) {
	const queryClient = useQueryClient();

	return useMutation(
		({ update, id }: UpdateObRuleRequest) =>
			apiStore
				.put({
					path: `/contract/${contractId}/ob-rule/${id}`,
					isAuthRequired: true,
					body: update,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
			onError: (error: any) => {
				toast.errorGenericObRule(error?.error?.detail || "Something went wrong");
			},
		}
	);
}

export function useBatchUpdateObRules(contractId: string) {
	const queryClient = useQueryClient();

	return useMutation(
		(param: BatchUpdateObRulesRequest) =>
			apiStore
				.put({
					path: `/contract/${contractId}/ob-rule`,
					isAuthRequired: true,
					body: param,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
			onError: (error: any) => {
				toast.errorGenericObRule(error?.error?.detail || "Something went wrong");
			},
		}
	);
}

/** Create a contract. Toast-silent: the create page owns the feedback. */
export function useCreateContract() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, CreateOrUpdateContractRequest>(
		(body) => {
			if (typeof body.paymentDays === "string") body.paymentDays = parseInt(body.paymentDays);
			if (body.prices) normalizeContractPrices(body.prices);

			return apiStore
				.post({
					path: `/contract`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
			},
		}
	);
}

export function useUpdateContract() {
	const queryClient = useQueryClient();

	return useMutation(
		({ contractId, update }: { contractId: string; update: CreateOrUpdateContractRequest }) =>
			apiStore
				.put({
					path: `/contract/${contractId}`,
					isAuthRequired: true,
					body: update,
				})
				.then((d) => d.data),

		{
			onSuccess: () => {
				queryClient.invalidateQueries(["contracts"]);
				toast.successGeneric();
			},
			onError: () => {
				toast.errorGeneric();
			},
		}
	);
}
